import "./PromotoresPage.css";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { PromotorResponse } from "../../../shared/response/terceros/PromotorResponse";
import { PromotorStatsResponse } from "../../../shared/response/terceros/PromotorStatsResponse";
import { PromotorCarteraItem } from "../../../shared/response/terceros/PromotorCarteraItem";
import { PromotorHistorialItem } from "../../../shared/response/terceros/PromotorHistorialItem";
import { PromotorService } from "../../services/terceros/PromotorService";
import { PromotorFormModal } from "./PromotorFormModal";
import { ClientesPage } from "./ClientesPage";
import { ProductosMarca } from "../../productosMarca";
import * as echarts from "echarts";

const AVATAR_COLORS = [
  "#0095E2", "#2EB054", "#8B5CF6", "#EF9F27", "#E24B4A",
  "#0EA5E9", "#F59E0B", "#10B981", "#6366F1", "#EC4899",
];

function initials(name: string): string {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function fmtCOP(v: number): string {
  if (!v) return "$0";
  return "$" + Math.round(v).toLocaleString("es-CO");
}

function fmtPct(v: number): string {
  return v.toFixed(v === Math.round(v) ? 0 : 1) + "%";
}

/** Coacciona un int64 (number o string de dígitos) a milisegundos; NaN si no aplica. */
function toMs(v: number | string | null | undefined): number {
  if (v === null || v === undefined || v === "") return NaN;
  if (typeof v === "number") return v;
  const s = v.trim();
  return /^-?\d+$/.test(s) ? Number(s) : new Date(s).getTime();
}

/** Formatea una fecha unix (int64) a fecha corta; "—" si no es válida. */
function fmtTs(v: number | string | null | undefined): string {
  const t = toMs(v);
  return isNaN(t) ? "—" : new Date(t).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
}

const TIPO_LABELS: Record<string, string> = {
  interno: "Promotor interno",
  externo: "Promotor externo",
  distribuidor: "Distribuidor",
};

type PeriodKey = "7d" | "30d" | "90d" | "year";

const PERIOD_LABELS: Record<PeriodKey, string> = {
  "7d": "Últimos 7 días",
  "30d": "Últimos 30 días",
  "90d": "Últimos 90 días",
  year: "Últimos 12 meses",
};

const GRANULARIDAD_LABELS: Record<string, string> = {
  dia: "por día",
  semana: "por semana",
  mes: "por mes",
};

const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

/** Formatea la etiqueta ISO de un bucket: "2026-08" → "Ago 26"; "2026-08-19" → "19 Ago". */
function fmtBucketLabel(iso: string): string {
  const parts = iso.split("-");
  const mes = MESES_CORTOS[parseInt(parts[1], 10) - 1] ?? "";
  return parts.length === 2 ? `${mes} ${parts[0].slice(2)}` : `${parts[2]} ${mes}`;
}

/**
 * Chip de acceso al sistema. El usuario de plataforma lo crea el backend al crear el
 * promotor, así que "Sin acceso" solo aparece en promotores anteriores a esa vinculación.
 */
function accesoChip(p: { usuarioPlataformaId: string | null }): string {
  return p.usuarioPlataformaId
    ? `<span class="pm-pill pm-p-ok" title="Tiene usuario de plataforma con rol comercial"><span class="pm-dot"></span>Con acceso</span>`
    : `<span class="pm-pill pm-p-na" title="Promotor creado antes de la vinculación automática: todavía no puede iniciar sesión">Sin acceso</span>`;
}

const SERVICIO_LABELS: Record<string, string> = {
  dataag: "DATA AG",
  rastro: "RASTRO",
  bascula: "Báscula",
  vista: "VISTA",
  protec: "PROTEC",
};

export class PromotoresPage extends BasePageComponent {
  private readonly _service = PromotorService.instance;

  /**
   * `_productos`: lo que vende la marca (2026-10-20). Lo demás no sale en la distribución por servicio, en los servicios
   * de la cartera ni en los productos del historial (ver `ProductosMarca`).
   */
  constructor(private readonly _productos: ProductosMarca) {
    super();
  }

  private _promotores: PromotorResponse[] = [];

  // DOM list
  private _listContent!: HTMLDivElement;
  private _tbody!: HTMLTableSectionElement;
  private _totEl!: HTMLSpanElement;
  private _searchInput!: HTMLInputElement;

  // DOM detail
  private _detailView!: HTMLDivElement;

  // Detail state
  private _currentPromotor: PromotorResponse | null = null;
  private _stats: PromotorStatsResponse | null = null;
  private _cartera: PromotorCarteraItem[] = [];
  private _historial: PromotorHistorialItem[] = [];
  private _chart: echarts.ECharts | null = null;
  private _period: PeriodKey = "90d";

  public getAppRoute(): string { return "/terceros/promotores"; }
  public getPageTitle(): string | null { return "Promotores"; }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page pm-page";
    pageContent.innerHTML = `
      <div class="pm-list-content">
        <div class="pm-pg-hd">
          <div>
            <div class="pm-pg-ey">Módulo comercial</div>
            <div class="pm-pg-ti">Promotores</div>
            <div class="pm-pg-su">Internos · distribuidores · KPIs · detalle</div>
          </div>
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="pm-nuevo-btn">
            <i class="ph ph-plus" aria-hidden="true"></i> Nuevo promotor
          </button>
        </div>
        <div class="pm-frow">
          <div class="pm-sw">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input class="pm-si" id="pm-search" placeholder="Buscar por nombre, email o teléfono...">
          </div>
          <span class="pm-ftot" id="pm-tot">0 promotores</span>
        </div>
        <div class="pm-tbl-wrap">
          <table class="pm-tbl">
            <thead><tr>
              <th>Promotor</th>
              <th>Tipo</th>
              <th>Teléfono</th>
              <th>Acceso</th>
              <th></th>
            </tr></thead>
            <tbody id="pm-tbody"></tbody>
          </table>
        </div>
      </div>
      <div class="pm-detail-view"></div>
    `;

    this._listContent = pageContent.querySelector<HTMLDivElement>(".pm-list-content")!;
    this._detailView  = pageContent.querySelector<HTMLDivElement>(".pm-detail-view")!;
    this._tbody       = document.getElementById("pm-tbody")! as HTMLTableSectionElement;
    this._totEl       = document.getElementById("pm-tot")!;
    this._searchInput = document.getElementById("pm-search")! as HTMLInputElement;

    document.getElementById("pm-nuevo-btn")!.onclick = () => this._onCreateClick();
    this._searchInput.oninput = () => this._filterAndRender();

    this._loadData();
  }

  protected override beforeDispose(): void {
    this._chart?.dispose();
    this._chart = null;
  }

  // ── List ──

  private _filterAndRender(): void {
    const q = this._searchInput.value.toLowerCase();
    const list = this._promotores.filter(
      (p) =>
        (p.nombre   ?? "").toLowerCase().includes(q) ||
        (p.email    ?? "").toLowerCase().includes(q) ||
        (p.telefono ?? "").includes(q),
    );
    this._renderTable(list);
  }

  private _renderTable(list: PromotorResponse[]): void {
    this._totEl.textContent = `${list.length} promotor${list.length === 1 ? "" : "es"}`;

    if (list.length === 0) {
      this._tbody.innerHTML = `<tr><td colspan="5"><div class="pm-empty">
        <i class="ph ph-users" aria-hidden="true"></i>
        <div class="pm-empty-title">Sin promotores</div>
        <div class="pm-empty-desc">No hay promotores que coincidan con la búsqueda.</div>
      </div></td></tr>`;
      return;
    }

    this._tbody.innerHTML = list.map((p, i) => {
      const tipoLabel = TIPO_LABELS[p.tipo] || p.tipo || "—";
      return `<tr data-idx="${i}">
        <td>
          <div style="display:flex;align-items:center;gap:10px;">
            <div class="pm-avatar" style="background:${avatarColor(p.nombre)}">${initials(p.nombre)}</div>
            <div>
              <div class="pm-cell-name">${this._esc(p.nombre)}</div>
              <div class="pm-cell-email">${this._esc(p.email)}</div>
            </div>
          </div>
        </td>
        <td><span class="pm-tipo-pill">${this._esc(tipoLabel)}</span></td>
        <td style="font-size:13px;color:var(--sx-text-secondary)">${this._esc(p.telefono)}</td>
        <td>${accesoChip(p)}</td>
        <td>
          <div class="pm-actions">
            <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm pm-edit-btn" data-idx="${i}" aria-label="Editar">
              <i class="ph ph-pencil" aria-hidden="true"></i>
            </button>
          </div>
        </td>
      </tr>`;
    }).join("");

    this._tbody.querySelectorAll("tr").forEach((tr) => {
      tr.onclick = (e) => {
        if ((e.target as HTMLElement).closest(".pm-edit-btn")) return;
        const idx = Number(tr.dataset.idx);
        this._goToDetail(list[idx]);
      };
    });

    this._tbody.querySelectorAll<HTMLButtonElement>(".pm-edit-btn").forEach((btn) => {
      btn.onclick = (e) => {
        e.stopPropagation();
        this._onEditClick(list[Number(btn.dataset.idx)]);
      };
    });
  }

  // ── Data loading ──

  private async _loadData(): Promise<void> {
    try {
      this._promotores = await this._service.get();
      this._filterAndRender();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar promotores", (error as Error).message);
    }
  }

  // ── Navigation ──

  private async _goToDetail(promotor: PromotorResponse): Promise<void> {
    this._currentPromotor = promotor;
    // Los stats del promotor anterior no aplican aquí: sin esto el primer render
    // pintaría sus cifras hasta que llegara la respuesta.
    this._stats = null;
    this._listContent.style.display = "none";
    this._detailView.classList.add("active");
    this._renderDetail(promotor);
    await this._loadDetailData(promotor);
  }

  private _goToList(): void {
    this._chart?.dispose();
    this._chart = null;
    this._detailView.classList.remove("active");
    this._listContent.style.display = "";
  }

  // ── Detail data ──

  private async _loadDetailData(promotor: PromotorResponse): Promise<void> {
    const [desde, hasta] = this._periodRange();

    // Se limpian los datos del período anterior antes de pedir los nuevos: si la
    // petición falla, los renders muestran "—" en vez de dejar en pantalla las
    // cifras del período viejo (que hacía parecer que el filtro no funcionaba).
    this._stats = null;
    this._renderStatsLoading();

    const [stats, cartera, historial] = await Promise.all([
      PromotorService.instance.getStats(promotor.id, desde, hasta)
        .catch((error: Error) => { this._onDetailError("estadísticas", error); return null; }),
      PromotorService.instance.getCartera(promotor.id)
        .catch((error: Error) => { this._onDetailError("cartera", error); return [] as PromotorCarteraItem[]; }),
      PromotorService.instance.getHistorial(promotor.id, desde, hasta)
        .catch((error: Error) => { this._onDetailError("historial", error); return [] as PromotorHistorialItem[]; }),
    ]);

    // El usuario pudo cambiar de promotor mientras la petición estaba en vuelo.
    if (this._currentPromotor?.id !== promotor.id) return;

    this._stats     = stats;
    this._cartera   = cartera;
    this._historial = historial;

    this._renderStats();
    this._renderCartera();
    this._renderVsEquipo();
    this._renderDistribucion();
    this._renderHistorial();
    this._initChart();
  }

  private _onDetailError(que: string, error: Error): void {
    errorMessageComponent.addError(`Error al cargar ${que} del promotor`, error.message);
  }

  private _periodRange(): [string, string] {
    const desde = new Date();
    const hasta = new Date();
    switch (this._period) {
      case "7d":  desde.setDate(desde.getDate() - 7); break;
      case "30d": desde.setDate(desde.getDate() - 30); break;
      case "90d": desde.setDate(desde.getDate() - 90); break;
      case "year": desde.setFullYear(desde.getFullYear() - 1); break;
    }
    // Rango de días completos: desde el inicio del día inicial hasta el fin del día de hoy,
    // para no cortar oportunidades creadas más tarde en el día (evita el borde por zona horaria).
    desde.setHours(0, 0, 0, 0);
    hasta.setHours(23, 59, 59, 999);
    return [desde.toISOString(), hasta.toISOString()];
  }

  // ── Detail render ──

  private _renderDetail(promotor: PromotorResponse): void {
    const tipoLabel = TIPO_LABELS[promotor.tipo] || promotor.tipo || "—";
    const activoHtml = promotor.isActivo
      ? `<span class="pm-pill pm-p-ok"><span class="pm-dot"></span>Activo</span>`
      : `<span class="pm-pill pm-p-er"><span class="pm-dot"></span>Inactivo</span>`;
    const tipoHtml = tipoLabel !== "—" ? `<span class="pm-pill pm-p-bl">${this._esc(tipoLabel)}</span>` : "";

    this._detailView.innerHTML = `
      <div class="pm-det-topbar">
        <button class="pm-det-back" id="pm-det-back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          Promotores
        </button>
        <div class="pm-det-sep"></div>
        <span class="pm-det-breadcrumb">${this._esc(promotor.nombre)}</span>
        <div class="pm-det-sp"></div>
        <span class="pm-det-badge">DATA AG</span>
        <button class="pm-det-edit" id="pm-det-edit">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          Editar
        </button>
      </div>
      <div class="pm-scroll">
        <div class="pm-det-hd">
          <div class="pm-det-hd-top">
            <div class="pm-det-av" style="background:${avatarColor(promotor.nombre)}">${initials(promotor.nombre)}</div>
            <div class="pm-det-hd-info">
              <div class="pm-det-hd-name">${this._esc(promotor.nombre)}</div>
              <div class="pm-det-hd-sub">${this._esc(promotor.email)} &nbsp;·&nbsp; ${this._esc(promotor.telefono)}</div>
              <div class="pm-det-hd-meta">
                ${activoHtml}
                ${tipoHtml}
                ${accesoChip(promotor)}
              </div>
            </div>
            <div class="pm-det-hd-actions">
              <button class="pm-det-btn-call" id="pm-det-call">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.65 3.42 2 2 0 0 1 3.62 1.24h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.13 6.13l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                Llamar
              </button>
              <button class="pm-det-btn-msg" id="pm-det-msg">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                Mensaje
              </button>
            </div>
          </div>
        </div>

        <div class="pm-period-bar">
          <span class="pm-p-lbl">Período:</span>
          <div class="pm-p-btns">${this._periodBtnsHTML()}</div>
        </div>

        <div class="pm-kpis" id="pm-kpis">${this._kpisHTML()}</div>

        <div class="pm-tabs">
          <div class="pm-tab on" data-tab="rendimiento">Rendimiento</div>
          <div class="pm-tab" data-tab="cartera">Cartera de clientes</div>
          <div class="pm-tab" data-tab="historial">Historial comercial</div>
        </div>

        <div class="pm-tp on" id="pm-tp-rendimiento"></div>
        <div class="pm-tp" id="pm-tp-cartera"></div>
        <div class="pm-tp" id="pm-tp-historial"></div>
      </div>
    `;

    document.getElementById("pm-det-back")!.onclick  = () => this._goToList();
    document.getElementById("pm-det-edit")!.onclick  = () => this._onEditClick(promotor);
    document.getElementById("pm-det-call")!.onclick  = () => {
      if (promotor.telefono) window.location.href = `tel:${promotor.telefono}`;
    };

    this._detailView.querySelectorAll<HTMLElement>(".pm-tab").forEach((tab) => {
      tab.onclick = () => {
        this._detailView.querySelectorAll(".pm-tab").forEach((t) => t.classList.remove("on"));
        this._detailView.querySelectorAll(".pm-tp").forEach((t) => t.classList.remove("on"));
        tab.classList.add("on");
        const tp = document.getElementById(`pm-tp-${tab.dataset.tab}`);
        if (tp) tp.classList.add("on");
        if (tab.dataset.tab === "rendimiento") setTimeout(() => this._initChart(), 50);
      };
    });

    this._detailView.querySelectorAll<HTMLElement>(".pm-p-btn").forEach((btn) => {
      btn.onclick = () => {
        this._detailView.querySelectorAll(".pm-p-btn").forEach((b) => b.classList.remove("on"));
        btn.classList.add("on");
        this._period = btn.dataset.period as PeriodKey;
        if (this._currentPromotor) void this._loadDetailData(this._currentPromotor);
      };
    });

    this._renderRendimientoPlaceholder();
    this._renderCarteraPlaceholder();
    this._historialPlaceholder();
  }

  // ── Render KPIs ──

  /** Botones del selector de período pintados desde `_period`, para que el resaltado nunca se desincronice del filtro aplicado. */
  private _periodBtnsHTML(): string {
    const opciones: [PeriodKey, string][] = [["7d", "7d"], ["30d", "30d"], ["90d", "90d"], ["year", "Este año"]];
    return opciones
      .map(([key, label]) => `<button class="pm-p-btn${this._period === key ? " on" : ""}" data-period="${key}">${label}</button>`)
      .join("");
  }

  /**
   * Tarjetas de KPI. "Clientes en cartera" NO depende del período (el backend lo saca
   * de Cliente.PromotorId, no del pipeline) y por eso lleva la etiqueta explícita;
   * el resto sí se recalcula con el rango seleccionado.
   */
  private _kpisHTML(): string {
    const s = this._stats;
    const kpis = s
      ? [
          { val: String(s.clientesActivos), cls: "", lbl: "Clientes en cartera (total)" },
          { val: String(s.clientesNuevos), cls: "g", lbl: "Clientes nuevos" },
          { val: String(s.oportunidades), cls: "", lbl: "Cotizaciones" },
          { val: String(s.cierres), cls: "g", lbl: "Cierres" },
          { val: fmtCOP(s.ventasTotales), cls: "g", lbl: "Ventas totales" },
          { val: fmtPct(s.tasaConversion), cls: "g", lbl: "Conversión" },
        ]
      : [
          { val: "—", cls: "", lbl: "Clientes en cartera (total)" },
          { val: "—", cls: "", lbl: "Clientes nuevos" },
          { val: "—", cls: "", lbl: "Cotizaciones" },
          { val: "—", cls: "", lbl: "Cierres" },
          { val: "—", cls: "", lbl: "Ventas totales" },
          { val: "—", cls: "", lbl: "Conversión" },
        ];
    return kpis
      .map((k) => `<div class="pm-kc"><div class="pm-kc-v ${k.cls}">${k.val}</div><div class="pm-kc-l">${k.lbl}</div></div>`)
      .join("");
  }

  private _renderStats(): void {
    const el = document.getElementById("pm-kpis");
    if (!el) return;
    el.innerHTML = this._kpisHTML();
  }

  /** Deja los KPIs en "…" mientras se pide el nuevo período, para que se vea que el filtro se aplicó aunque el valor no cambie. */
  private _renderStatsLoading(): void {
    const el = document.getElementById("pm-kpis");
    if (!el) return;
    el.querySelectorAll<HTMLElement>(".pm-kc-v").forEach((v) => {
      v.className = "pm-kc-v";
      v.textContent = "…";
    });
  }

  // ── Tab: Rendimiento ──

  private _renderRendimientoPlaceholder(): void {
    const el = document.getElementById("pm-tp-rendimiento");
    if (!el) return;
    el.innerHTML = `<div style="padding:24px;text-align:center;color:var(--sx-text-tertiary);font-size:13px;">Cargando...</div>`;
  }

  private _renderVsEquipo(): void {
    const el = document.getElementById("pm-tp-rendimiento");
    if (!el) return;
    const s = this._stats;
    if (!s) { el.innerHTML = `<div style="padding:24px;text-align:center;color:var(--sx-text-tertiary);font-size:13px;">Sin datos para ${PERIOD_LABELS[this._period].toLowerCase()}</div>`; return; }

    const promoName = this._currentPromotor?.nombre || "Promotor";
    const vs = s.vsEquipo;

    el.innerHTML = `
      <div class="pm-action-banner pm-ab-green">
        <div class="pm-ab-ico pm-ico-green">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
        </div>
        <div class="pm-ab-body">
          <div class="pm-ab-t">${s.tasaConversion >= 40 ? "Top performer" : "Rendimiento estable"}</div>
          <div class="pm-ab-s">${promoName} tiene ${fmtPct(s.tasaConversion)} de conversión (promedio equipo: ${fmtPct(vs.tasaConversion)}).</div>
        </div>
      </div>

      <div class="pm-chart-wrap">
        <div class="pm-chart-title">Evolución de ventas y cotizaciones</div>
        <div class="pm-chart-sub">${PERIOD_LABELS[this._period]} · agrupado ${GRANULARIDAD_LABELS[s.granularidadGrafica] ?? "por mes"}</div>
        <div id="pm-chart-ev" style="height:180px;"></div>
      </div>

      <div class="pm-comp-grid">
        <div class="pm-tbl-card">
          <div class="pm-tbl-card-hd">
            <div class="pm-tbl-card-t">vs. Promedio del equipo</div>
            <div class="pm-tbl-card-s">${PERIOD_LABELS[this._period]}</div>
          </div>
          <table class="pm-tbl-detail">
            <thead><tr><th>Métrica</th><th>${this._esc(promoName)}</th><th>Promedio</th></tr></thead>
            <tbody>
              ${this._vsRow("Conversión", fmtPct(s.tasaConversion), fmtPct(vs.tasaConversion), s.tasaConversion > vs.tasaConversion)}
              ${this._vsRow("Cotizaciones", String(s.oportunidades), String(Math.round(vs.oportunidades)), s.oportunidades > vs.oportunidades)}
              ${this._vsRow("Ticket promedio", fmtCOP(s.ticketPromedio), fmtCOP(vs.ticketPromedio), s.ticketPromedio > vs.ticketPromedio)}
              ${this._vsRow("Clientes nuevos", String(s.clientesNuevos), String(Math.round(vs.clientesNuevos)), s.clientesNuevos > vs.clientesNuevos)}
            </tbody>
          </table>
        </div>

        <div class="pm-tbl-card">
          <div class="pm-tbl-card-hd">
            <div class="pm-tbl-card-t">Distribución por servicio</div>
            <div class="pm-tbl-card-s">Qué vende más</div>
          </div>
          <div id="pm-distribucion-body"></div>
        </div>
      </div>
    `;
  }

  private _vsRow(label: string, val: string, avgVal: string, better: boolean): string {
    return `<tr>
      <td>${label}</td>
      <td style="font-family:var(--sx-font-mono);${better ? "color:var(--sx-success-text);font-weight:600" : ""}">${val}</td>
      <td style="font-family:var(--sx-font-mono)">${avgVal}</td>
    </tr>`;
  }

  private _renderDistribucion(): void {
    const el = document.getElementById("pm-distribucion-body");
    if (!el) return;
    const s = this._stats;
    // Solo los servicios que vende la marca; el porcentaje es sobre ellos.
    const dist = s ? s.distribucionServicios.filter((d) => this._productos.ofrece(d.servicio)) : [];
    if (dist.length === 0) {
      el.innerHTML = `<div style="padding:16px;text-align:center;color:var(--sx-text-tertiary);font-size:12px;">Sin datos</div>`;
      return;
    }
    const total = dist.reduce((a, b) => a + b.cantidad, 0) || 1;
    el.innerHTML = `<table class="pm-tbl-detail"><thead><tr><th>Servicio</th><th>Cierres</th><th>% del total</th></tr></thead><tbody>
      ${dist.map((d) => {
        const pct = Math.round(d.cantidad / total * 100);
        const label = SERVICIO_LABELS[d.servicio] || d.servicio;
        return `<tr>
          <td>${label}</td>
          <td style="font-family:var(--sx-font-mono)">${d.cantidad}</td>
          <td><div class="pm-conv-wrap"><div class="pm-conv-track"><div class="pm-conv-fill pm-cf-g" style="width:${pct}%"></div></div></div></td>
        </tr>`;
      }).join("")}
    </tbody></table>`;
  }

  private _initChart(): void {
    const el = document.getElementById("pm-chart-ev");
    if (!el) return;
    this._chart?.dispose();
    this._chart = echarts.init(el);

    const s = this._stats;
    if (!s || s.graficaMensual.length === 0) {
      this._chart.setOption({
        title: { text: `Sin datos para ${PERIOD_LABELS[this._period].toLowerCase()}`, left: "center", top: "center", textStyle: { fontSize: 13, color: "#888" } },
      });
      return;
    }

    // El backend agrupa por día, semana o mes según el ancho del período; la etiqueta
    // llega en ISO ("yyyy-MM-dd" o "yyyy-MM") y se formatea aquí.
    const meses = s.graficaMensual.map((g) => fmtBucketLabel(g.mes));

    this._chart.setOption({
      tooltip: { trigger: "axis" },
      legend: { data: ["Ventas ($M)", "Cotizaciones"], bottom: 0, textStyle: { fontSize: 12 } },
      grid: { left: 50, right: 50, top: 16, bottom: 40 },
      xAxis: { type: "category", data: meses, axisLabel: { fontSize: 11 } },
      yAxis: [
        { type: "value", name: "Ventas ($M)", nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 11 }, splitLine: { lineStyle: { color: "#f0f0f0" } } },
        { type: "value", name: "Cotizaciones", nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 11 }, splitLine: { show: false } },
      ],
      series: [
        {
          name: "Ventas ($M)",
          type: "bar",
          data: s.graficaMensual.map((g) => (g.ventas / 1_000_000)),
          itemStyle: { color: "#0f6e56", borderRadius: [4, 4, 0, 0] },
        },
        {
          name: "Cotizaciones",
          type: "line",
          yAxisIndex: 1,
          data: s.graficaMensual.map((g) => g.oportunidades),
          lineStyle: { color: "#ef9f27", width: 2 },
          symbol: "circle",
          symbolSize: 6,
          itemStyle: { color: "#ef9f27" },
        },
      ],
    });
  }

  // ── Tab: Cartera ──

  private _renderCarteraPlaceholder(): void {
    const el = document.getElementById("pm-tp-cartera");
    if (!el) return;
    el.innerHTML = `<div style="padding:24px;text-align:center;color:var(--sx-text-tertiary);font-size:13px;">Cargando...</div>`;
  }

  private _renderCartera(): void {
    const el = document.getElementById("pm-tp-cartera");
    if (!el) return;
    const list = this._cartera;

    if (list.length === 0) {
      el.innerHTML = `<div style="padding:24px;text-align:center;color:var(--sx-text-tertiary);font-size:13px;">Sin clientes asignados</div>`;
      return;
    }

    const planPill = (c: PromotorCarteraItem): string => {
      if (!c.planActivo) return `<span class="pm-pill pm-p-na">Sin plan</span>`;
      if (c.fechaVencimientoPlataforma) {
        const dias = Math.ceil((new Date(c.fechaVencimientoPlataforma).getTime() - Date.now()) / 86_400_000);
        if (dias < 0)  return `<span class="pm-pill pm-p-er"><span class="pm-dot"></span>Vencido</span>`;
        if (dias <= 30) return `<span class="pm-pill pm-p-am"><span class="pm-dot"></span>Por vencer</span>`;
      }
      return `<span class="pm-pill pm-p-ok"><span class="pm-dot"></span>Activo</span>`;
    };

    const servBadges = (c: PromotorCarteraItem): string => {
      const sv = (c.serviciosActivos || []).filter((id) => this._productos.ofrece(id));
      if (sv.length === 0) return `<span class="pm-pill pm-p-na">Sin servicios</span>`;
      return sv.map((s) => `<span class="pm-pill pm-p-bl">${SERVICIO_LABELS[s] || s}</span>`).join(" ");
    };

    el.innerHTML = `
      <div class="pm-section-hd">
        <div><div class="pm-section-t">Clientes en cartera</div><div class="pm-section-s">${list.length} cliente${list.length !== 1 ? "s" : ""} asignados · cartera completa, no depende del período</div></div>
      </div>
      <div class="pm-tbl-card">
        <table class="pm-tbl-detail">
          <thead><tr><th>Cliente</th><th>Servicios</th><th>Última oportunidad</th><th>Estado plan</th><th>Acción</th></tr></thead>
          <tbody>${list.map((c) => {
            const haStr = c.hectareasTotales > 0 ? ` · ${c.hectareasTotales.toFixed(1)} ha` : "";
            const fincasStr = c.cantFincas ? `${c.cantFincas} finca${c.cantFincas > 1 ? "s" : ""}${haStr}` : "—";
            return `<tr>
              <td><div style="font-weight:500">${this._esc(c.nombre)}</div><div style="font-size:11px;color:var(--sx-text-tertiary)">${fincasStr}</div></td>
              <td>${servBadges(c)}</td>
              <td style="font-family:var(--sx-font-mono);font-size:11px">${fmtTs(c.ultimaOportunidadFecha)}</td>
              <td>${planPill(c)}</td>
              <td><button class="sx-btn sx-btn-outline sx-btn-sm" data-cliente-id="${c.id}">Ver cliente</button></td>
            </tr>`;
          }).join("")}</tbody>
        </table>
      </div>
    `;

    el.querySelectorAll<HTMLButtonElement>("button[data-cliente-id]").forEach((btn) => {
      btn.onclick = () => {
        ClientesPage.openClienteId = btn.dataset.clienteId!;
        document.dispatchEvent(new CustomEvent("app:navigate", { detail: "/terceros/clientes" }));
      };
    });
  }

  // ── Tab: Historial ──

  private _historialPlaceholder(): void {
    const el = document.getElementById("pm-tp-historial");
    if (!el) return;
    el.innerHTML = `<div style="padding:24px;text-align:center;color:var(--sx-text-tertiary);font-size:13px;">Cargando...</div>`;
  }

  private _renderHistorial(): void {
    const el = document.getElementById("pm-tp-historial");
    if (!el) return;

    const ops = this._historial;
    if (ops.length === 0) {
      el.innerHTML = `<div style="padding:24px;text-align:center;color:var(--sx-text-tertiary);font-size:13px;">Sin actividad comercial registrada</div>`;
      return;
    }

    const stagePill = (s: string): string => {
      const map: Record<string, string> = {
        new: "pm-p-bl", contacted: "pm-p-bl", demo: "pm-p-am",
        proposal: "pm-p-am", closed_won: "pm-p-ok", closed_lost: "pm-p-er",
      };
      const cls = map[s] || "pm-p-na";
      const label =
        s === "closed_won" ? "Ganado" :
        s === "closed_lost" ? "Perdido" :
        s.charAt(0).toUpperCase() + s.slice(1);
      return `<span class="pm-pill ${cls}">${label}</span>`;
    };

    const prodBadges = (op: PromotorHistorialItem): string => {
      if (!op.productos) return "";
      const badges: string[] = [];
      if (op.productos.dataag) badges.push("DATA AG");
      // Solo lo que la marca vende.
      if (op.productos.rastro > 0 && this._productos.ofrece("rastro")) badges.push(`RASTRO×${op.productos.rastro}`);
      if (op.productos.bascula > 0 && this._productos.ofrece("bascula")) badges.push(`Báscula×${op.productos.bascula}`);
      if (op.productos.vista && this._productos.ofrece("vista")) badges.push("VISTA");
      if (op.productos.protec.activo && this._productos.ofrece("protec")) badges.push("PROTEC");
      return badges.length > 0
        ? badges.map((b) => `<span class="pm-pill pm-p-bl">${b}</span>`).join(" ")
        : `<span class="pm-pill pm-p-na">Sin productos</span>`;
    };

    const activas = ops.filter((o) => o.stage !== "closed_won" && o.stage !== "closed_lost").length;
    const clientesUnicos = new Set(ops.map((o) => o.clienteId)).size;

    el.innerHTML = `
      <div class="pm-section-hd">
        <div>
          <div class="pm-section-t">Pipeline Comercial</div>
          <div class="pm-section-s">· ${activas} oportunidad${activas !== 1 ? "es" : ""} activas · ${clientesUnicos} cliente${clientesUnicos !== 1 ? "s" : ""} registrados</div>
        </div>
      </div>
      <div class="pm-tbl-card">
        <table class="pm-tbl-detail">
          <thead><tr><th>Cliente</th><th>Etapa</th><th>Origen</th><th>Productos</th><th>Creado</th></tr></thead>
          <tbody>${ops.map((op) => {
            return `<tr>
              <td>
                <div style="font-weight:500">${this._esc(op.clienteNombre)}</div>
              </td>
              <td>${stagePill(op.stage)}</td>
              <td style="font-size:12px;color:var(--sx-text-secondary)">${this._esc(op.source)}</td>
              <td>${prodBadges(op)}</td>
              <td style="font-family:var(--sx-font-mono);font-size:11px;white-space:nowrap">${fmtTs(op.createdAt)}</td>
            </tr>`;
          }).join("")}</tbody>
        </table>
      </div>
    `;
  }

  // ── Actions ──

  private _onCreateClick(): void {
    new PromotorFormModal((saved) => {
      this._promotores = [saved, ...this._promotores];
      this._filterAndRender();
      // El backend crea el usuario de plataforma y encola el correo; el front solo avisa.
      errorMessageComponent.addSuccess(
        "Promotor creado",
        saved.email
          ? `Se envió un correo de bienvenida a ${saved.email} con sus credenciales de acceso.`
          : "Sin email no se creó usuario de plataforma: el promotor no podrá iniciar sesión.",
      );
    }).init();
  }

  private _onEditClick(item: PromotorResponse): void {
    new PromotorFormModal((saved) => {
      this._promotores = this._promotores.map((p) => (p.id === saved.id ? saved : p));
      this._filterAndRender();
      if (this._currentPromotor?.id === saved.id) {
        this._currentPromotor = saved;
        this._renderDetail(saved);
      }
    }, item).init();
  }

  private _esc(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
